import { TFunction } from "i18next";
import { User } from "../../../../core/models/entities/User";
import { DataError } from "../../../../core/errors/DataError";
import { WELCOME_MAIL_NOT_SENT_ERROR_CODE } from "../../../../data/models/mappers/mapError";
import { enviroment } from "../../../../enviroment";
import { formatDate } from "../../../../utils/functions/DateUtil";
import { parseUtcStamp } from "../../../../utils/functions/storageUpdatedAt";

/**
 * User deactivation (flag userDeactivateEnabled): the pure rules behind the
 * "Deactivated" chip, the deactivate/reactivate toasts and the two API
 * refusals the whole app must recognise - derived ONLY from the fields GET
 * users?orgId carries and the API's own error envelopes.
 */

/** The API's 403 on every call of a deactivated user: { code, errorCode: "userDeactivated" }. */
export const USER_DEACTIVATED_ERROR_CODE = "userDeactivated";
/** PATCH …/status answered 409: the caller's own account. */
export const SELF_DEACTIVATE_CODE = "selfDeactivate";
/** PATCH …/status answered 409: the organisation's last active user. */
export const LAST_ACTIVE_USER_CODE = "lastActiveUser";
/** PATCH …/status answered 502 on a reactivation: the B2C account could not be re-enabled (nothing changed). */
export const LOGIN_ACCOUNT_NOT_ENABLED = 502;
/** POST users/create answered 502 (API flag UserCreateHardeningEnabled): the user exists, the welcome mail did not go out. */
export const WELCOME_MAIL_NOT_SENT_ON_CREATE = 502;
/** react-query's default: three retries. */
const PROFILE_QUERY_DEFAULT_RETRIES = 3;

type StatusFields = Pick<User, "deactivatedUtc" | "deactivatedBy">;

/** The one definition of "deactivated" on the client: the stamp is set. */
export function isUserDeactivated(user: StatusFields): boolean {
  return !!user.deactivatedUtc;
}

/** A Date for an ISO stamp (a zoneless stamp is UTC); undefined when absent/unparsable. */
function stampToDate(iso: string | null | undefined): Date | undefined {
  if (!iso) return undefined;
  const ms = parseUtcStamp(iso);
  if (Number.isNaN(ms) || ms <= 0) return undefined;
  return new Date(ms);
}

/**
 * The chip's tooltip "Deactivated {{date}} by {{by}}" - only when the stamp
 * parses; a blank "by" is shown as a dash. undefined = no tooltip.
 */
export function deactivatedTitle(
  user: StatusFields,
  t: TFunction
): string | undefined {
  const at = stampToDate(user.deactivatedUtc);
  if (!at) return undefined;
  return t("customerUpdate.userDeactivatedTitle", {
    date: formatDate(at),
    by: (user.deactivatedBy ?? "").trim() || "—",
    interpolation: { escapeValue: false },
  });
}

function statusOf(error: unknown) {
  return error instanceof DataError ? error.errorStatus : undefined;
}

function messageOf(error: unknown): string {
  const status = statusOf(error);
  return (
    (typeof status?.message === "string" && status.message.trim()) ||
    (error instanceof Error && error.message.trim()) ||
    ""
  );
}

/**
 * The error toast of a failed status change. The two 409 refusals and the
 * reactivation's 502 get their translated texts (the same facts the API's
 * English messages state; the UI language wins); anything else = the generic
 * text plus whatever message the error carries (403 wrong org, 404 flag off,
 * gateway text - all human-readable).
 */
export function setUserActiveErrorMessage(
  error: unknown,
  active: boolean,
  t: TFunction
): string {
  const status = statusOf(error);
  if (status?.errorCode === SELF_DEACTIVATE_CODE) {
    return t("customerUpdate.deactivateSelfRefused");
  }
  if (status?.errorCode === LAST_ACTIVE_USER_CODE) {
    return t("customerUpdate.deactivateLastUserRefused");
  }
  if (active && status?.code === LOGIN_ACCOUNT_NOT_ENABLED) {
    return t("customerUpdate.reactivateLoginAccountFailed");
  }
  const generic = t(
    active
      ? "customerUpdate.reactivateUserFailed"
      : "customerUpdate.deactivateUserFailed"
  );
  const message = messageOf(error);
  return message ? `${generic}: ${message}` : generic;
}

/** True for the API's 403 userDeactivated envelope (the caller's own account is deactivated). */
export function isUserDeactivatedError(error: unknown): boolean {
  const status = statusOf(error);
  return status?.code === 403 && status.errorCode === USER_DEACTIVATED_ERROR_CODE;
}

/**
 * True for the create endpoint's 502 welcomeMailNotSent envelope, recognised
 * by the API's stable marker (`error: "welcomeMailNotSent"`, kept as
 * errorCode by mapError under the flag) - never by the English message. A
 * gateway/host page (HTML, "Bad Gateway", empty) for a request the function
 * never ran carries no marker: there the user was NOT created, and
 * refetching would show nothing new.
 */
export function isCreateUserMailNotSentError(error: unknown): boolean {
  const status = statusOf(error);
  return (
    status?.code === WELCOME_MAIL_NOT_SENT_ON_CREATE &&
    status.errorCode === WELCOME_MAIL_NOT_SENT_ERROR_CODE
  );
}

/**
 * The profile query's retry rule (flag userDeactivateEnabled): react-query's
 * default (3 retries) for everything, EXCEPT the API's 403 userDeactivated -
 * that answer is final (the caller's own account is deactivated), so the
 * logout fires after the first call instead of after four calls and ~7 s.
 * Only installed with the flag on (the repository leaves the option out
 * otherwise = exactly today's default).
 */
export function profileQueryRetry(failureCount: number, error: unknown): boolean {
  if (isUserDeactivatedError(error)) return false;
  return failureCount < PROFILE_QUERY_DEFAULT_RETRIES;
}

/**
 * The admin act-as picker (flag userDeactivateEnabled): a deactivated user
 * cannot be acted as (the API refuses the order too) - left out. Flag off =
 * the list exactly as fetched (the same array instance).
 */
export function usersSelectableForActAs<T extends StatusFields>(
  users: T[] | undefined
): T[] | undefined {
  if (!enviroment.userDeactivateEnabled || !users) return users;
  return users.filter((u) => !isUserDeactivated(u));
}
